import { z } from 'zod'

/**
 * The props schema of every registry component, keyed by id (#77).
 *
 * JSX-free on purpose: `renderConfigV2.ts` derives the `component` item union
 * from this map, and the server imports that schema as a value. The React
 * components themselves live in `./index.tsx`, keyed by the same ids, and
 * `registry.test.ts` checks the two maps name the same set.
 */

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, {
  message: 'Expected a #rrggbb colour',
})

export const COMPONENT_PROPS = {
  'title-card': z.strictObject({
    title: z.string().max(200),
    subtitle: z.string().max(200).optional(),
  }),
  'logo-outro': z.strictObject({
    text: z.string().max(120).optional(),
  }),
  /**
   * A visualizer is a named look (#546), not geometry: the creator picks a
   * look and places it, as with any artwork, and `visualizerGeometry` owns
   * the numbers behind each one. `bars` is the look every visualizer drew
   * before the looks existed; the rest are the v1 layouts' bar styles.
   */
  'audio-visualizer': z.strictObject({
    variant: z.enum([
      'bars',
      'bold',
      'thin',
      'equalizer',
      'blocks',
      'mirrored',
      'radial',
      'hills',
      'wave',
    ]),
    color: hexColor.optional(),
  }),
  /**
   * A picture fitted in its box: a fixed URL (the host avatar, a logo badge),
   * or with `source: 'speaker'` the avatar of whoever is speaking on the
   * item's `for` track — then `url` is unused, and nothing shows when nobody
   * speaks or the speaker has no avatar.
   */
  picture: z.strictObject({
    source: z.enum(['url', 'speaker']).optional(),
    url: z.string().url().max(2048).optional(),
    fit: z.enum(['cover', 'contain']),
    /** Read only while `radius` is unset (a picture saved before it). */
    shape: z.enum(['rect', 'rounded', 'circle']),
    /** Corner radius in pixels; half the shorter side or more is round. */
    radius: z.number().min(0).max(10000).optional(),
    borderColor: hexColor.optional(),
    borderWidth: z.number().min(0).max(100).optional(),
  }),
} as const

export type ComponentId = keyof typeof COMPONENT_PROPS
export type ComponentProps<K extends ComponentId> = z.infer<
  (typeof COMPONENT_PROPS)[K]
>

export const COMPONENT_IDS = Object.keys(COMPONENT_PROPS) as ComponentId[]

export const COMPONENT_DEFAULTS: { [K in ComponentId]: ComponentProps<K> } = {
  'title-card': { title: 'Title', subtitle: '' },
  'logo-outro': { text: 'Made with Zealous' },
  'audio-visualizer': { variant: 'bars' },
  picture: {
    url: 'https://assets.zealous.app/static/demo/community-call-v1.jpg',
    fit: 'cover',
    shape: 'circle',
  },
}

export const COMPONENT_NAMES: { [K in ComponentId]: string } = {
  'title-card': 'Title card',
  'logo-outro': 'Logo outro',
  'audio-visualizer': 'Audio visualizer',
  picture: 'Picture',
}
